<template>
  <div class="container-fluid py-4 pb-5 mb-5 night-audit-page">
    <!-- Page Title -->
    <div class="d-flex justify-content-between align-items-center flex-wrap mb-4" style="gap:10px;">
      <h3 class="mb-0 font-weight-bold text-primary">Night Audit</h3>
      <div>
        <date-range-picker :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true" v-model="datePicker" class="custom-width-datepicker">
          <template #input="picker">
            <span v-if="picker.startDate" class="text-dark">{{ formatPickerDate(picker.startDate) }}</span>
            <span v-else class="text-muted"><i class="bi bi-calendar-event mr-1"></i>Select date</span>
          </template>
        </date-range-picker>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted">Loading night audit...</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- Audit Data -->
    <div v-if="auditData && !loading" class="audit-content">
      <!-- Date and Run Info -->
      <div class="card mb-4 shadow-sm">
        <div class="card-body d-flex justify-content-between align-items-center">
          <div>
            <h5 class="mb-0">Audit for {{ auditData.date }}</h5>
            <small class="text-muted">
              Run at: {{ auditData.audit_meta.run_at }} by {{ auditData.audit_meta.run_by }}
            </small>
          </div>
        </div>
      </div>

      <!-- Room Overview -->
     <div class="card">
            <div class="card-body">
                <div class="row">
                    <div class="col-md-4 col-sm-12">
                        <h4 class="font-weight-bold h4">Rooms Overview</h4>
                    <ul class="list-group list-group-flush">
                        <li class="m-1 py-1 border-bottom border-light" v-for="(value, key) in auditData.rooms" :key="key">

                        <p class="text-uppercase text-muted">{{ formatLabel(key) }} : <span class="badge badge-secondary py-1 px-1">{{ value }}</span></p>



                    </li>
                    </ul>
                    </div>
                    <div class="col-md-4 col-sm-12">
                        <h4 class="font-weight-bold h4">Revenues</h4>
                    <ul class="list-group list-group-flush">
                         <li class="m-1 py-1 border-bottom border-light" v-for="(value, key) in auditData.revenue" :key="key">

                        <p class="text-uppercase text-muted">{{ formatLabel(key) }} : <span class="badge badge-secondary py-1 px-1">{{ convertCurrencyToLocal(value) }}</span></p>



                    </li>
                    </ul>
                    </div>
                </div>


      </div>
      </div>



      <!-- Payments Section -->
      <div class="card mb-4 shadow-sm">
        <div class="card-header bg-light">
          <h6 class="mb-0 font-weight-bold">Payments</h6>
        </div>
        <div class="card-body">
          <div class="row text-center">
            <div class="col-12 col-md-4 mb-3">
              <h6 class="text-muted">Incoming Total</h6>
              <h4 class="font-weight-bold text-success">
                KSH {{ formatCurrency(auditData.payments.incoming_total) }}
              </h4>
            </div>
            <div class="col-12 col-md-4 mb-3">
              <h6 class="text-muted">Refunds</h6>
              <h4 class="font-weight-bold text-danger">
                KSH {{ formatCurrency(auditData.payments.refunds_total) }}
              </h4>
            </div>
            <div class="col-12 col-md-4 mb-3">
              <h6 class="text-muted">By Method</h6>
              <ul class="list-unstyled mb-0">
                <li
                  v-for="(amount, method) in auditData.payments.by_method"
                  :key="method"
                  class="small"
                >
                  {{ method }}: <strong>KSH {{ formatCurrency(amount) }}</strong>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Exceptions Section -->
      <div class="card mb-4 shadow-sm">
        <div class="card-header bg-light">
          <h6 class="mb-0 font-weight-bold">Exceptions</h6>
        </div>
        <div class="card-body row text-center">
          <div class="col-12 col-md-4 mb-3">
            <h6 class="text-muted">Discounts Total</h6>
            <h4 class="font-weight-bold">KSH {{ formatCurrency(auditData.exceptions.discounts_total) }}</h4>
          </div>
          <div class="col-12 col-md-4 mb-3">
            <h6 class="text-muted">Refunds Total</h6>
            <h4 class="font-weight-bold">KSH {{ formatCurrency(auditData.exceptions.refunds_total) }}</h4>
          </div>
          <div class="col-12 col-md-4 mb-3">
            <h6 class="text-muted">No-Shows Flagged</h6>
            <h4 class="font-weight-bold">{{ auditData.exceptions.no_shows_flagged }}</h4>
          </div>
        </div>
      </div>

      <!-- Counts Summary -->
      <div class="card mb-4 shadow-sm">
        <div class="card-header bg-light">
          <h6 class="mb-0 font-weight-bold">Summary</h6>
        </div>
        <div class="card-body row text-center">
          <div class="col-12 col-md-6 mb-3">
            <h6 class="text-muted">Reservations Processed</h6>
            <h4 class="font-weight-bold">{{ auditData.counts.reservations_processed }}</h4>
          </div>
          <div class="col-12 col-md-6 mb-3">
            <h6 class="text-muted">Orders Count</h6>
            <h4 class="font-weight-bold">{{ auditData.counts.orders_count }}</h4>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';

export default {
  components: { DateRangePicker },
  data() {
    return {
      selectedDate: new Date().toISOString().slice(0, 10), // default to today
      auditData: null,
      loading: false,
      error: null
    }
  },
  computed: {
    datePicker: {
      get() { return { startDate: this.selectedDate ? new Date(this.selectedDate) : null }; },
      set(val) {
        this.selectedDate = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : '';
        this.fetchAudit();
      },
    },
  },
  created() {
    this.fetchAudit()
  },
  methods: {
    formatPickerDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    async fetchAudit() {
      this.loading = true
      this.error = null
      this.auditData = null

      try {
        const response = await axios.post(`/api/reservations/night-audit`)



        this.auditData = response.data
      } catch (err) {
        this.error = err.message
      } finally {
        this.loading = false
      }
    },
    formatCurrency(amount) {
      return amount.toLocaleString('en-KE', { minimumFractionDigits: 2 })
    },
    formatLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }
}
</script>

<style scoped>
.night-audit-page {
  background: #f8f9fa;
  min-height: 100vh;
}

.card {
  border: none;
  border-radius: 0.75rem;
}

.card-header {
  border-bottom: none;
}

.card-body h4 {
  color: #343a40;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}
</style>
